import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'motion/react';
import produce from 'immer';
import { useDebounce } from 'react-use';
import { AiComponentCssRule } from '@/pkg/ai';
import { ParsedCssRule } from '@/features/editor/widgets/shared/ai-editing-layout/manipulators/use-css-manipulator';
import { CssNumericUnitInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-numeric-unit-input';
import {
  ALL_FIELDS_OPTIONS,
  COLOR_FIELDS,
  defaultCssDeclarationValue,
  ENUM_FIELD_VALUES,
  ENUM_FIELDS,
  NUMERIC_FIELDS,
  NUMERIC_UNIT_FIELDS,
  SPACING_FIELDS,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/css-declarations';
import { CssNumericInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-numeric-input';
import { CssSpacingInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-spacing-input';
import { CssEnumInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-enum-input';
import { CssColorInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-color-input';
import { CssBorderInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-border-input';
import { CssBoxShadowInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-box-shadow-input';
import { CssTextShadowInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-text-shadow-input';
import { CssFontFamilyInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/css-font-family-input';
import { AddNewPropertyLayout } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/css/add-new-property-layout';
import { StackedInputGroupWrapper } from '../shared/shared';

export interface GenericCssRuleInputProps {
  component: AiComponentCssRule;
  getCssRule: (selector: string) => ParsedCssRule | undefined;
  setCssRule: (input: ParsedCssRule) => void;
}

export function GenericCssRuleInput({
  component,
  getCssRule,
  setCssRule,
}: GenericCssRuleInputProps) {
  const [refreshCount, setRefreshCount] = React.useState(0);
  const loadedRule = useMemo(
    () => getCssRule(component.selector),
    [component, refreshCount],
  );
  const [newPropertyShown, setNewPropertyShown] = React.useState(false);

  const [rule, setRule] = React.useState<ParsedCssRule>(loadedRule);

  const onDeclarationChange = (name: string, value: string) => {
    if (!rule) return;
    let changes = 0;
    const updated = produce(rule, (draft) => {
      draft.declarations.map((decl) => {
        if (decl.name === name && decl.value !== value) {
          changes += 1;
          decl.value = value;
        }
        return decl;
      });
    });

    if (changes > 0) {
      setRule(updated);
    }
  };

  useDebounce(
    () => {
      if (rule) {
        setCssRule(rule);
      }
    },
    1000,
    [rule],
  );

  useEffect(() => {
    if (refreshCount > 1) {
      loadedRule && setRule(loadedRule);
    }
  }, [refreshCount]);

  const existingProperties = useMemo(
    () => rule.declarations.map((decl) => decl.name),
    [rule],
  );

  if (existingProperties.length === 0) {
    return null;
  }

  const onNewProperty = () => {
    setNewPropertyShown(true);
  };

  const onCancelNewProperty = () => {
    setNewPropertyShown(false);
  };

  const onAddProperties = (properties: string[]) => {
    if (!rule) return;

    const updated = produce(rule, (draft) => {
      properties.forEach((property) => {
        draft.declarations.push({
          name: property,
          value: defaultCssDeclarationValue(property) || `none`,
        });
      });
    });

    setRule(updated);
    setCssRule(updated);
    setNewPropertyShown(false);
    setRefreshCount((c) => c + 1);
  };

  const declarations = rule?.declarations?.filter((decl) =>
    ALL_FIELDS_OPTIONS.find((i) => i.value === decl.name),
  );

  if (declarations?.length <= 0) {
    return null;
  }

  return (
    <StackedInputGroupWrapper>
      <AnimatePresence mode="popLayout">
        {declarations.map((decl) => (
          <motion.div
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: `auto` }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ ease: `linear`, duration: 0.2 }}
            key={decl.name}
          >
            <div>
              {NUMERIC_UNIT_FIELDS.includes(decl.name) && (
                <CssNumericUnitInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
              {NUMERIC_FIELDS.includes(decl.name) && (
                <CssNumericInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
              {SPACING_FIELDS.includes(decl.name) && (
                <CssSpacingInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
              {ENUM_FIELDS.includes(decl.name) && (
                <CssEnumInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                  options={ENUM_FIELD_VALUES[decl.name] || []}
                />
              )}
              {COLOR_FIELDS.includes(decl.name) && (
                <CssColorInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
              {decl.name === `border` && (
                <CssBorderInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
              {decl.name === `box-shadow` && (
                <div>
                  <CssBoxShadowInput
                    name={decl.name}
                    initialValue={decl.value}
                    onChange={onDeclarationChange}
                  />
                </div>
              )}
              {decl.name === `text-shadow` && (
                <CssTextShadowInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
              {decl.name === `font-family` && (
                <CssFontFamilyInput
                  name={decl.name}
                  initialValue={decl.value}
                  onChange={onDeclarationChange}
                />
              )}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {!newPropertyShown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <AddNewPropertyBtn onClick={onNewProperty}>
              Add Property
            </AddNewPropertyBtn>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {newPropertyShown && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: `auto` }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ ease: `linear`, duration: 0.2 }}
          >
            <AddNewPropertyLayout
              existingProperties={existingProperties}
              onCancel={onCancelNewProperty}
              onAdd={onAddProperties}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </StackedInputGroupWrapper>
  );
}

const AddNewPropertyBtn = styled(motion.button)`
  appearance: none;
  outline: none;
  border: none;
  width: 100%;
  background-color: rgba(224, 234, 243, 0.4);
  margin-top: 1rem;
  padding: 1rem 0rem;
  color: #c0c5cc;
  border-radius: 1rem;
  font-size: 1.5rem;
  cursor: pointer;

  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: rgba(224, 234, 243, 0.6);
  }

  &:active {
    background-color: rgba(224, 234, 243, 0.8);
  }
`;
